import CONFIG from "./config";

export interface StreamCallbacks {
  onChunk: (text: string) => void;
  onSessionId: (sessionId: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

export async function sendMessageStream(
  prompt: string,
  idToken: string,
  callbacks: StreamCallbacks,
  runtimeSessionId?: string
): Promise<void> {
  const body: Record<string, string> = { prompt };
  if (runtimeSessionId) body.runtimeSessionId = runtimeSessionId;

  const response = await fetch(`${CONFIG.apiUrl}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: idToken },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    callbacks.onError(`API error: ${response.status}`);
    return;
  }

  const contentType = response.headers.get("content-type") || "";

  // Non-streamed response (guardrail intervention, errors)
  if (contentType.includes("application/json")) {
    const data = await response.json();
    if (data.runtimeSessionId) callbacks.onSessionId(data.runtimeSessionId);
    callbacks.onChunk(data.result || data.error || JSON.stringify(data));
    callbacks.onDone();
    return;
  }

  // Streamed NDJSON response
  const reader = response.body?.getReader();
  if (!reader) { callbacks.onError("No response body"); return; }

  const decoder = new TextDecoder();
  let buffer = "";
  let sessionCaptured = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const parsed = JSON.parse(line);
        if (parsed.chunk) callbacks.onChunk(parsed.chunk);
        if (!sessionCaptured && parsed.runtimeSessionId) {
          callbacks.onSessionId(parsed.runtimeSessionId);
          sessionCaptured = true;
        }
      } catch { /* skip malformed lines */ }
    }
  }

  callbacks.onDone();
}

export async function ping(idToken: string): Promise<void> {
  await fetch(`${CONFIG.apiUrl}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: idToken },
    body: JSON.stringify({ type: "ping" }),
  });
}
